import type { LocationData, LocationDataRaw } from '../common/location';
import type { Orientation } from '../common/orientation';
import type { Vector, XYVector } from '../common/vector';
import type { DeviceJSONObject } from './device';

export interface Capture {
	/**
	 * Reference to the actual capture objectId if it exists
	 * This was added on 2019-03-14 most captures will not have this value.
	 */
	objectId?: string;
	/**
	 * Information about the camera.
	 */
	camera: CaptureCamera;
	/**
	 * Details of the laser used.
	 *
	 * Available for photomeasure captures.
	 */
	laser?: {
		raw?: CaptureRawLaserData;
		/**
		 * Distance to the capture (Meters)
		 */
		distance: number;
	};
	/**
	 * Location of the device and the captured object.
	 *
	 * Available for photomeasure captures.
	 */
	locations?: CaptureLocation;
	/**
	 * The orientation of the device.
	 */
	orientation: CaptureOrientation;
	/**
	 * Time the capture was taken.
	 */
	timestamp: number;
	/**
	 * Type of capture (truesize)
	 */
	type: string;
	/**
	 * Unique ID of this capture.
	 */
	uuid: string;

	/**
	 * Device serial number
	 */
	device: string;

	/**
	 * Snapshot of the CaptureFile's `raw.role`, copied in on collection save so consumers that read only
	 * collection data (e.g. the photo-measure summary) can resolve the MidSpan pair without fetching capture
	 * documents. The CaptureFile stays the source of truth; this copy refreshes on every save.
	 * @see CaptureRole
	 */
	role?: CaptureRole;
}

export interface CaptureRawOrientationData {
	/**
	 * Raw sensor data from the magnetometer, [x, y, z] (Gauss)
	 */
	mag: number[];
	/**
	 * Raw sensor data from the accelerometer, [x, y, z] (m/s^2)
	 */
	accel: number[];
	/**
	 * Raw sensor data from the gyroscope, [x, y, z] (Radian/s)
	 */
	gyro: number[];
	/**
	 * Yaw as computed from IMU Sensor fusion (Radians)
	 * @minimum -3.141592653589793
	 * @maximum 3.141592653589793
	 */
	yaw: number;
	/**
	 * Pitch as computed from IMU Sensor fusion (Radians)
	 * @minimum -1.5707963267948966
	 * @maximum 1.5707963267948966
	 */
	pitch: number;
	/**
	 * Roll as computed from IMU Sensor fusion (Radians)
	 * @minimum -3.141592653589793
	 * @maximum 3.141592653589793
	 */
	roll: number;
}

export interface CaptureRawData {
	/** Image UUID */
	uuid?: string;
	laser?: CaptureRawLaserData;
	/** @See CaptureOrientation */
	orientation?: {
		magneticnorm: number;
		quality: string;
		declination: number;
		calibration?: string;
		accel?: Vector;
		gyro?: Vector;
		mag?: Vector;
		pitch?: number;
		roll?: number;
		yaw?: number;
	};
	gps?: LocationDataRaw;
	device?: DeviceJSONObject;
	/**
	 * What this capture is within a multi-shot tool. Set by IKE Field; absent for ordinary single captures.
	 * @see CaptureRole
	 */
	role?: CaptureRole;
}

/**
 * Role a capture plays within a multi-shot capture tool. The dedicated MidSpan tool (UFO-1082) pairs a span
 * shot with a ground reference shot taken from the same position; the ground shot is what the locked base
 * marker is derived from.
 *
 * Authoritative when present. Captures without a role fall back to their order in the field value.
 */
export const CaptureRole = {
	MIDSPAN_SPAN: 'midspan.span' as const,
	MIDSPAN_GROUND: 'midspan.ground' as const,
};
export type CaptureRole = typeof CaptureRole.MIDSPAN_SPAN | typeof CaptureRole.MIDSPAN_GROUND;

export interface CaptureRawLaserData {
	distance: number;
	offset: number;
	version: number;
	wireMode?: boolean;
}

export interface CaptureOrientation extends Orientation {
	/**
	 * Local magnetic field strength (Gauss)
	 */
	magneticnorm?: number;
	/**
	 * Realtime confidence flag output by the vn-100 IMU
	 * 2 Bit quality output encoded as a String
	 */
	quality?: string;
	/**
	 * Local magnetic declination according to the IMU's world model. (degrees)
	 * @see World Magnetic Mode [WMM (2014-2019)]
	 * @see http://www.ngdc.noaa.gov/geomag-web/
	 */
	declination?: number;
	/**
	 * UUID of compass calibration
	 * @see ImuCalibration table database
	 */
	calibration?: string;
	/**
	 * Raw sensor values
	 */
	raw?: CaptureRawOrientationData;
}

export interface CaptureCamera {
	/**
	 * Camera calibration
	 */
	calibration: CaptureCameraCalibration;
	/**
	 * UUID of the image.
	 */
	imageuuid: string;
	/**
	 * How zoomed was the camera when the image was captured.
	 * @minimum 0
	 */
	zoom?: number;
}

export interface CaptureCameraCalibration {
	/**
	 * Taylor series constants, approximating the radial distortion.
	 */
	distortion: number[];

	/**
	 * World coordiante physical translation (Meters)
	 */
	translation: Vector;

	/**
	 * Offset of the Laser to the camera (Piexls)
	 */
	center: XYVector;

	/**
	 * Rotation of the image sensor in relation to the lense (Radians)
	 */
	rotation: Vector;

	/**
	 * Focal length of the image plane, assuming square pixels (Pixels)
	 */
	focal: number;
}

export interface CaptureLocation {
	/**
	 * Location of the captured object.
	 */
	target: LocationData;
	/**
	 * Location of the device.
	 */
	device: LocationData;
}

export interface CaptureCapture {
	capture: Capture;
}
